import { EmbeddableVirtualConnectionSubplaceWithoutRouter } from '@tableau/catalog-ui-temp';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { catalogApolloClient } from '~src/catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '~src/catalog/CatalogFeatureFlagsAdapter';
import { getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import { DataCatalogUIActionsService } from '~src/catalog/services/DataCatalogUIActionsService';
import { Messages } from '~src/codegen/Localize';
import { ExtractTaskDefinition } from '~src/extractTask/components/spaces/contentDefinitions/ExtractTaskDefinition';
import { IPublishedConnectionDetailInfo, IPublishedConnectionInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { IPublishedConnectionActions } from '~src/publishedConnection/serverApi/types/IPublishedConnectionActions';
import { PublishedConnectionTableDefinition } from '~src/publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { RequestAccessAction } from '~src/requestAccess/actions/RequestAccessAction';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs, } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { PublishedConnectionSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { PublishedConnectionSubplaceProps } from '../types/PublishedConnectionSubplaceProps';
import { PublishedConnectionScheduledTasksSubplace } from './PublishedConnectionScheduledTasksSubplace';
import { PublishedConnectionSpaceHeader } from './PublishedConnectionSpaceHeader';

export type PublishedConnectionSpaceStateProps = Readonly<{
  contentCountsPublishedConnection: ISpaceInfoTypeItemCount<'virtualConnection'>;
  contentCountsUpToDate: boolean;
  isServerOrSiteAdmin: boolean;
  urls: SpacesSiteUrls;
  catalogingOnSiteEnabled: boolean;
  areUsersVisible: boolean;
}>;

export type PublishedConnectionSpaceDispatchProps = Readonly<{
  addToFavorites: () => void;
  enterSpace: (requestContent: boolean) => void;
  fetchUpdatedContentCounts: () => void;
  markContentCountsUpToDate: () => void;
  removeFromFavorites: () => void;
  setPreviousUrl: (previousUrl: string) => void;
}>;

export type PublishedConnectionSpaceOwnProps = Readonly<{
  publishedConnectionAllowedActions: IPublishedConnectionActions;
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  publishedConnectionInfo: IPublishedConnectionInfo;
  projectHierarchy: INamedItemInfo[];
  refreshData: () => void;

  // subplace info
  readonly subplaceName: string;
  readonly siteUrls: SiteUrls;
  readonly grantAccessDisplay: boolean;
  readonly grantAccessUrl: string;
}>;

export interface PublishedConnectionSpaceProviderState {
  readonly initialRender: boolean;
}

const InitialState: PublishedConnectionSpaceProviderState = {
  initialRender: true,
};

export type PublishedConnectionSpaceProps =
  PublishedConnectionSpaceStateProps
  & PublishedConnectionSpaceDispatchProps
  & PublishedConnectionSpaceOwnProps;

function tablesSubspace(subplaceProps: PublishedConnectionSubplaceProps): Subspace {
  return explorerSubspace({
    contentCount: getContentCount(subplaceProps.contentCountsPublishedConnection, 'tables'),
    contentCountUpToDate: subplaceProps.contentCountsUpToDate,
    emptyStateProps: {
      primaryMessage: Messages.publishedConnectionTablesEmpty()
    },
    hideSelection: false,
    name: 'tables',
    shouldRenderContentCount: true,
    supportedDisplayModes: PublishedConnectionTableDefinition.supportedDisplayModes,
    supportFilterPanel: false,
    tabLabel: Messages.publishedConnectionTablesTabHeader(),
    toolbarVisibility: 'enabled',
  });
}

function scheduledTasksSubspace(subplaceProps: PublishedConnectionSubplaceProps): Subspace {
  if (FeatureFlags.vconnRunHistory) {
    return explorerSubspace({
      name: 'scheduledTasks',
      tabLabel: Messages.scheduledTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      supportFilterPanel: false,
      hideSelection: true,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: subplaceProps.publishedConnectionAllowedActions.refreshExtract
          ? Messages.publishedConnectionNoScheduledTasksCreateNewMessage()
          : Messages.publishedConnectionNoScheduledTasksMessage()
      }
    });
  } else {
    return {
      name: 'scheduledTasks',
      renderContent: () => (
        <PublishedConnectionScheduledTasksSubplace
          editActionsAllowed={subplaceProps.publishedConnectionAllowedActions.refreshExtract}
          isServerOrSiteAdmin={subplaceProps.isServerOrSiteAdmin}
          publishedConnectionDetailInfo={subplaceProps.publishedConnectionDetailInfo}
          updateScheduledTasksCount={subplaceProps.fetchUpdatedContentCounts}
        />
      ),
      shouldRenderTabContentCount: true,
      tabContentCount: getContentCount(subplaceProps.contentCountsPublishedConnection, 'scheduledTasks'),
      tabContentCountUpToDate: subplaceProps.contentCountsUpToDate,
      tabLabel: Messages.publishedConnectionScheduledTasksTabHeader(),
    };
  }
}


function runHistorySubspace(): Subspace {
  if (FeatureFlags.vconnRunHistory)
    return explorerSubspace({
      name: 'runHistory',
      tabLabel: Messages.runHistory(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      supportFilterPanel: false,
      hideSelection: true,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.publishedConnectionEmptyRunHistory()
      }
    });
}

function lineageSubspace(
  publishedConnection: IPublishedConnectionDetailInfo,
  siteUrls: SiteUrls,
  areUsersVisible: boolean,
  siteLuid: string,
): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <EmbeddableVirtualConnectionSubplaceWithoutRouter
        vizportalUrlId={publishedConnection.id}
        client={catalogApolloClient}
        siteUrls={siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
        telemetryContext={getTelemetryCommonProperties()}
        siteLuid={siteLuid}
      />
  };
}

function getPublishedConnectionDetailWithUsage(
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo,
  publishedConnectionInfo: IPublishedConnectionInfo,
) {
  return {
    usageInfo: publishedConnectionInfo.usageInfo,
    usageInfoMaxValues: publishedConnectionInfo.usageInfoMaxValues,
    ...publishedConnectionDetailInfo,
  };
}

function getPublishedConnectionSubspaces(subplaceProps: PublishedConnectionSubplaceProps) {
  const hasExtracts = (subplaceProps.publishedConnectionDetailInfo.tables.filter(table => table.hasExtract === true)).length > 0;
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  return _.compact([
    tablesSubspace(subplaceProps),
    hasExtracts && scheduledTasksSubspace(subplaceProps),
    hasExtracts && runHistorySubspace(),
    FeatureFlags.publishedConnectionsLineageEnabled &&
    subplaceProps.catalogingOnSiteEnabled &&
    lineageSubspace(subplaceProps.publishedConnectionDetailInfo, subplaceProps.siteUrls, subplaceProps.areUsersVisible, siteLuid),
  ]);
}

function getContentCount(contentCountsPublishedConnection: ISpaceInfoTypeItemCount<'virtualConnection'>, contentName: string): number {
  return contentCountsPublishedConnection && contentCountsPublishedConnection[contentName as PublishedConnectionSubspaceName];
}

function getTableExtractStatusMessage(
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo,
) {
  const tables = publishedConnectionDetailInfo.tables;
  const tablesWithExtracts = tables.filter(table => table.hasExtract === true);
  if (tables.length > 0 && tablesWithExtracts.length > 0 && tablesWithExtracts.length !== tables.length) {
    return Messages.liveAndExtract();
  }
  return undefined;
}

export class PublishedConnectionSpace extends React.PureComponent<PublishedConnectionSpaceProps, PublishedConnectionSpaceProviderState> {
  private memoizedPublishedConnectionDetailWithUsage = defaultMemoize(getPublishedConnectionDetailWithUsage);
  private memoizedGetPublishedConnectionSubspaces = defaultMemoize(getPublishedConnectionSubspaces);

  constructor(props: PublishedConnectionSpaceProps) {
    super(props);
    this.state = InitialState;
    props.enterSpace(true);
  }

  public urlFromSubspaceName = (subspaceName?: PublishedConnectionSubspaceName) => {
    switch (subspaceName) {
      case 'tables':
        return this.props.urls.virtualConnectionOverviewUrl(this.props.publishedConnectionDetailInfo.id);
      case 'scheduledTasks':
        return this.props.urls.virtualConnectionScheduledTasksUrl(this.props.publishedConnectionDetailInfo.id);
      case 'lineage':
        return this.props.urls.virtualConnectionLineageUrl(this.props.publishedConnectionDetailInfo.id);
      case 'runHistory':
        return this.props.urls.virtualConnectionRunHistoryUrl(this.props.publishedConnectionDetailInfo.id);
    }
  };

  public render() {
    const subplaceProps: PublishedConnectionSubplaceProps = {
      contentCountsPublishedConnection: this.props.contentCountsPublishedConnection,
      contentCountsUpToDate: this.props.contentCountsUpToDate,
      fetchUpdatedContentCounts: this.props.fetchUpdatedContentCounts,
      isServerOrSiteAdmin: this.props.isServerOrSiteAdmin,
      publishedConnectionAllowedActions: this.props.publishedConnectionAllowedActions,
      publishedConnectionDetailInfo: this.props.publishedConnectionDetailInfo,
      refreshData: this.props.refreshData,
      siteUrls: this.props.urls,
      areUsersVisible: this.props.areUsersVisible,
      catalogingOnSiteEnabled: this.props.catalogingOnSiteEnabled
    };

    const subspaces = this.memoizedGetPublishedConnectionSubspaces(subplaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as PublishedConnectionSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'virtualConnection'}
        spaceItemName={this.props.publishedConnectionDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionDetailInfo);
    this.changeSubspace(true);
    this.updateBreadcrumbs();
  }

  public componentDidUpdate(prevProps: PublishedConnectionSpaceProps) {
    if (this.props.publishedConnectionDetailInfo.id !== prevProps.publishedConnectionDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionDetailInfo);
      this.changeSubspace(true);
      this.updateBreadcrumbs();
    } else if (this.props.subplaceName !== prevProps.subplaceName) {
      this.changeSubspace(false);
    }
    if (this.state.initialRender) {
      this.setState({ initialRender: false }, () => {
        if (this.props.grantAccessDisplay) {
          RequestAccessAction.handleGrantAccess(this.props.publishedConnectionDetailInfo, this.props.grantAccessUrl);
          this.resetPreviousUrl();
        }
      });
    }
  }

  private shouldRequestContent() {
    // tasks are fetched from within the component, so no need to request via mixed content action
    switch (this.props.subplaceName) {
      case 'tables':
        return true;
      case 'scheduledTasks':
        return false;
      case 'runHistory':
        return true;
    }
  }

  private changeSubspace(didPublishedConnectionChange: boolean) {
    this.props.enterSpace(this.shouldRequestContent());
    if (didPublishedConnectionChange) {
      this.props.fetchUpdatedContentCounts();
    } else {
      // enterSpace action causes counts out of date.
      // We mark it upto date because there is no need to fetch count again for same published connection.
      this.props.markContentCountsUpToDate();
    }
  }

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.props.publishedConnectionDetailInfo], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const publishedConnectionDetailWithUsage = this.memoizedPublishedConnectionDetailWithUsage(
      this.props.publishedConnectionDetailInfo,
      this.props.publishedConnectionInfo,
    );

    return (
      <PublishedConnectionSpaceHeader
        itemInfo={publishedConnectionDetailWithUsage}
        tableExtractStatusLabel={FeatureFlags.hybridLiveExtractVConnsEnabled ? getTableExtractStatusMessage(this.props.publishedConnectionDetailInfo) : undefined}
        availableActions={this.props.publishedConnectionAllowedActions}
        openDetail={() => null}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        urls={this.props.urls}
        catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
      />
    );
  }

  private resetPreviousUrl = () => {
    const cleanDSUrls = window.location.href;
    this.props.setPreviousUrl(cleanDSUrls);
  };
}
